import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseEditCopyService, IdDescription, ReportHtml } from 'tms-lib';
import {
  BillOfMaterialsItem,
  CustomerPrices,
  Product,
  ProductAdditionalProcessing,
  ProductCost,
  ProductCustomProductSpecs,
  ProductDeliveryItem,
  ProductDetails,
  ProductExtraItem,
  ProductForSalesOrderLine,
  ProductProductType,
  ProductsForCreatePurchaseOrder,
  ProductSpecPrint,
  ProductUsedIn,
  PurchaseOrderLineProduct,
  PurchaseOrderMultiProductCreate,
  SalesOrderMultiProductCreate,
  SampleLabel,
  StockExposure,
  StockForecasting,
} from '../../../models';

@Injectable({
  providedIn: 'root',
})
export class ProductService extends BaseEditCopyService<Product, ProductDetails> {
  public refreshDetails = new Subject<void>();

  constructor() {
    super('Product', $localize`Product`);
  }

  getProductForSalesOrder(
    id: string,
    customerId: string,
    searchText: string,
    salesOrderHeaderId: string,
  ): Observable<ProductForSalesOrderLine> {
    return this.httpService.get<ProductForSalesOrderLine>(
      `${this.apiUrl}/GetProductForSalesOrder?productId=${id}&customerId=${customerId}&productSearchText=${searchText}&salesOrderHeaderId=${salesOrderHeaderId}`,
    );
  }

  getProductForPurchaseOrder(
    id: string,
    supplierId: string,
    purchaseOrderHeaderId: string,
  ): Observable<PurchaseOrderLineProduct> {
    return this.httpService.get<PurchaseOrderLineProduct>(
      `${this.apiUrl}/GetProductForPurchaseOrder?productId=${id}&supplierId=${supplierId}&purchaseOrderHeaderId=${purchaseOrderHeaderId}`,
    );
  }

  getForBillOfMaterials(search: string, productId: string): Observable<BillOfMaterialsItem[]> {
    return this.httpService.get<BillOfMaterialsItem[]>(
      `${this.apiUrl}/GetProductsForBillOfMaterials?search=${search}&productId=${productId}`,
    );
  }

  getForSalesOrderMultiCreate(search: string, salesOrderHeaderId: string): Observable<SalesOrderMultiProductCreate[]> {
    return this.httpService.get<SalesOrderMultiProductCreate[]>(
      `${this.apiUrl}/GetProductsForSalesOrderMultiCreate?search=${search}&salesOrderHeaderId=${salesOrderHeaderId}`,
    );
  }

  getForPurchaseOrderMultiCreate(
    search: string,
    purchaseOrderHeaderId: string,
  ): Observable<PurchaseOrderMultiProductCreate[]> {
    return this.httpService.get<PurchaseOrderMultiProductCreate[]>(
      `${this.apiUrl}/GetProductsForPurchaseOrderMultiCreate?search=${search}&purchaseOrderHeaderId=${purchaseOrderHeaderId}`,
    );
  }

  getProductsUsedIn(productId: string): Observable<ProductUsedIn[]> {
    return this.httpService.get<ProductUsedIn[]>(`${this.apiUrl}/GetProductsUsedIn?productId=${productId}`);
  }

  getDeliveryProduct(
    productId: string,
    countryId: string,
    postcode: string,
    chargeDelivery: boolean,
  ): Observable<ProductDeliveryItem> {
    return this.httpService.get<ProductDeliveryItem>(
      `${this.apiUrl}/GetDeliveryProduct?productId=${productId}&countryId=${countryId}&postcode=${postcode}&chargeDelivery=${chargeDelivery}`,
    );
  }

  getExtraProduct(productId: string): Observable<ProductExtraItem> {
    return this.httpService.get<ProductExtraItem>(`${this.apiUrl}/GetExtraProduct?productId=${productId}`);
  }

  checkKey(key: string, id: string): Observable<boolean> {
    return this.httpService.get<boolean>(`${this.apiUrl}/CheckKey?key=${key}&id=${id ?? ''}`);
  }

  checkStockExists(id: string, checked: boolean): Observable<boolean> {
    return this.httpService.get<boolean>(`${this.apiUrl}/CheckStockExists?id=${id}&checkedValue=${checked}`);
  }

  getProductsForCreatePurchaseOrder(
    search: string,
    supplierId = '',
    productTypeId = '',
    excludeSupplier = false,
  ): Observable<ProductsForCreatePurchaseOrder[]> {
    return this.httpService.get<ProductsForCreatePurchaseOrder[]>(
      `${this.apiUrl}/GetProductsForCreatePurchaseOrder?search=${search ?? ''}&supplierId=${supplierId ?? ''}&productTypeId=${productTypeId ?? ''}&excludeSupplier=${excludeSupplier}`,
    );
  }

  getDefaultProductTypeDetails(productTypeId: string): Observable<ProductProductType> {
    return this.httpService.get<ProductProductType>(`${this.apiUrl}/GetProductTypeDefaultDetails/${productTypeId}`);
  }

  getProductCostBreakdown(productId: string): Observable<ProductCost> {
    return this.httpService.get<ProductCost>(`${this.apiUrl}/ProductCostBreakdown?productId=${productId}`);
  }

  getProductByKey(productKey: string): Observable<Product> {
    return this.httpService.get<Product>(`${this.apiUrl}/ProductByKey?productKey=${productKey}`);
  }

  getProductForSpecPrint(productId: string): Observable<ProductSpecPrint> {
    return this.httpService.get<ProductSpecPrint>(`${this.apiUrl}/GetProductForSpecPrint?productId=${productId}`);
  }

  getProductCustomProductSpecs(productId: string): Observable<ProductCustomProductSpecs> {
    return this.httpService.get<ProductCustomProductSpecs>(
      `${this.apiUrl}/GetProductCustomProductSpecs?productId=${productId}`,
    );
  }

  getAdditionalProcessingProductDetails(productId: string): Observable<ProductAdditionalProcessing> {
    return this.httpService.get<ProductAdditionalProcessing>(
      `${this.apiUrl}/GetAdditionalProcessingProductDetails?productId=${productId}`,
    );
  }

  getStockForecasting(
    search: string,
    productId: string,
    monthsToAnalyse: number,
    showAllDetails: boolean,
    minimumStockCalculation: string,
    ignorePurchaseOrders: boolean,
    monthToStart: string,
    productTypeId = '',
    collectionProductId = '',
  ): Observable<StockForecasting[]> {
    return this.httpService.get<StockForecasting[]>(
      `${this.apiUrl}/StockForecasting?search=${search}&productId=${productId}&monthsToAnalyse=${monthsToAnalyse}&showAllDetails=${showAllDetails}&minimumStockCalculation=${minimumStockCalculation}&ignorePurchaseOrders=${ignorePurchaseOrders}&monthToStart=${monthToStart}&productTypeId=${productTypeId ?? ''}&collectionProductId=${collectionProductId ?? ''}`,
    );
  }

  getCustomerPrices(productId: string): Observable<CustomerPrices[]> {
    return this.httpService.get<CustomerPrices[]>(`${this.apiUrl}/CustomerPrices?productId=${productId}`);
  }

  stockExposureProducts(): Observable<IdDescription[]> {
    return this.httpService.get<IdDescription[]>(`${this.apiUrl}/StockExposureProducts`);
  }

  stockExposure(): Observable<StockExposure[]> {
    return this.httpService.get<StockExposure[]>(`${this.apiUrl}/StockExposure`);
  }

  getSampleLabelHtml(id: string): Observable<string> {
    return this.httpService
      .get<ReportHtml>(`${this.apiUrl}/GetSampleLabelHtml?id=${id}`)
      .pipe(map((model: ReportHtml) => model.html));
  }

  getSampleLabel(id: string): Observable<SampleLabel> {
    return this.httpService.get<SampleLabel>(`${this.apiUrl}/GetSampleLabel?id=${id}`);
  }

  exportStockExposure(): Observable<Blob> {
    return this.httpService.postForBlob(`${this.apiUrl}/ExportStockExposure`, '');
  }

  /** Summarised (banded) exposure for a single product — the one currently on screen. */
  exportStockExposureSummary(id: string): Observable<Blob> {
    return this.httpService.postForBlob(`${this.apiUrl}/ExportStockExposureSummary/${id}`, '');
  }

  updateIgnoreOnStockForecasting(id: string): Observable<string> {
    return this.httpService.post<string>(`${this.apiUrl}/UpdateIgnoreOnStockForecasting`, { id });
  }
}
